import { create } from 'zustand'
import type { ISuperPromptModel } from '@/lib/db/types'
import useSWR from 'swr'
import { fetcher } from '@/lib/utils'

// 定义 Store 状态类型
interface SuperPromptsState {
  // 数据状态
  superPrompts: ISuperPromptModel[]
  loading: boolean
  isSuperPromptsPanelOpen: boolean
  codingSuperPromptIds: string[] // 用于 coding 中的 super prompt ids
  // 操作方法
  setSuperPrompts: (superPrompts: ISuperPromptModel[]) => void

  setCodingSuperPromptIds: (superPromptIds: string[]) => void
  clearCodingSuperPromptIds: () => void
  getCodingSuperPrompts: () => ISuperPromptModel[]

  openSuperPromptsPanel: () => void
  closeSuperPromptsPanel: () => void
  fetchSuperPrompts: () => Promise<void>
  getSuperPromptById: (id: string) => ISuperPromptModel | undefined
  getSuperPromptsByCategory: (category: string) => ISuperPromptModel[]
}

// 创建 zustand store
export const useSuperPromptsStore = create<SuperPromptsState>((set, get) => ({
  loading: false,
  isSuperPromptsPanelOpen: false,

  // 所有 super_prompts
  superPrompts: [],

  // 用于 coding 中的 super prompt ids
  codingSuperPromptIds: [],

  setSuperPrompts: (superPrompts: ISuperPromptModel[]) => {
    set({ superPrompts })
  },

  setCodingSuperPromptIds: (superPromptIds: string[]) => {
    set({ codingSuperPromptIds: superPromptIds })
  },

  clearCodingSuperPromptIds: () => {
    set({ codingSuperPromptIds: [] })
  },

  getCodingSuperPrompts: () => {
    const { superPrompts, codingSuperPromptIds } = get()
    return superPrompts.filter((prompt: ISuperPromptModel) =>
      codingSuperPromptIds.includes(prompt._id?.toString() || ''),
    )
  },

  // 打开 super_prompts 面板
  openSuperPromptsPanel: () => {
    set({ isSuperPromptsPanelOpen: true })
    // Close editor panel when super prompts panel opens
    if (typeof window !== 'undefined') {
      // Import dynamically to avoid circular dependencies
      import('@/hooks/use-editor-store').then(({ useEditorStore }) => {
        const { turnOff } = useEditorStore.getState()
        turnOff()
      })
    }
  },

  // 关闭 super_prompts 面板
  closeSuperPromptsPanel: () => {
    set({ isSuperPromptsPanelOpen: false })
  },

  // 获取所有 super-prompts
  fetchSuperPrompts: async () => {
    try {
      set({ loading: true })

      const response = await fetch('/api/super-prompt')

      if (!response.ok) {
        // Try to extract error message from response
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
            if (errorData.message) {
              errorMessage += `: ${errorData.message}`
            }
          }
        } catch {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage)
      }

      const result = await response.json()

      if (result.data) {
        set({
          superPrompts: result.data,
          loading: false,
        })
      } else {
        throw new Error('Invalid response format')
      }
    } catch (error) {
      set({
        loading: false,
      })
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch super prompts'
      console.error('Failed to fetch super prompts:', errorMessage, error)
    }
  },

  // 获取所有需要展示在 ui 上的 super_prompts
  getSuperPromptsForUi: () => {
    const { superPrompts } = get()
    return superPrompts.filter(
      (prompt: ISuperPromptModel) =>
        prompt.status === 'published' && !prompt.hidden_in_menu_and_will_detect,
    )
  },

  // 根据 ID 获取单个 super-prompt
  getSuperPromptById: (id: string) => {
    const { superPrompts } = get()
    return superPrompts.find(
      (prompt: ISuperPromptModel) => prompt._id?.toString() === id,
    )
  },

  // 根据分类获取 super-prompts
  getSuperPromptsByCategory: (category: string) => {
    const { superPrompts } = get()
    return superPrompts.filter(
      (prompt: ISuperPromptModel) => prompt.category === category,
    )
  },
}))

export const useFetchSuperPrompts = () => {
  const { setSuperPrompts } = useSuperPromptsStore()

  const { data, error, mutate } = useSWR<{ data: ISuperPromptModel[] }>(
    '/api/super-prompt',
    fetcher,
    {
      onSuccess: (data) => {
        setSuperPrompts(data.data)
      },
    },
  )

  return {
    data,
    error,
    mutate,
  }
}
